<template>
    <section class="sc-card sc-detail-card">
        <!-- 미선택 -->
        <div v-if="!schdCtrlIdx" class="sc-empty">
            <i class="fas fa-external-link-alt sc-empty-icon" aria-hidden="true"></i>
            <span>{{ $t("제어 스케줄을 선택하세요.") }}</span>
        </div>

        <div v-else-if="!detail" class="sc-empty">
            <span>{{ loading ? $t("불러오는 중...") : $t("상세 정보를 불러오지 못했습니다.") }}</span>
        </div>

        <template v-else>
            <div class="sc-detail-head">
                <div class="sc-detail-top">
                    <span class="sc-status-badge" :class="{ 'is-off': detail.enabled !== 'Y' }">
                        {{ detail.enabled === "Y" ? $t("사용 중") : $t("미사용") }}
                    </span>
                    <span class="sc-id-badge">ID {{ detail.schdCtrlIdx }}</span>
                    <span style="flex: 1"></span>
                    <button type="button" class="sc-btn sc-btn--outline" @click="$emit('edit', detail)">
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M11 2.5l2.5 2.5L5 13.5 2 14l.5-3z" />
                        </svg>
                        {{ $t("편집") }}
                    </button>
                    <button
                        type="button"
                        class="sc-btn sc-btn--ghost sc-btn--icon"
                        :aria-label="$t('닫기')"
                        @click="$emit('close')"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                        >
                            <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
                        </svg>
                    </button>
                </div>
                <h2 class="sc-detail-name">{{ detail.schdCtrlName }}</h2>
                <div class="sc-detail-desc">
                    {{ zoneLabel }} &nbsp;·&nbsp; {{ detail.startDate || "-" }} ~ {{ detail.endDate || "-" }}
                    <template v-if="detail.description"><br />{{ detail.description }}</template>
                </div>
                <div class="sc-tabs" role="tablist">
                    <button
                        v-for="t in tabs"
                        :key="t.key"
                        type="button"
                        role="tab"
                        class="sc-tab"
                        :class="{ 'is-active': tab === t.key }"
                        :aria-selected="String(tab === t.key)"
                        @click="tab = t.key"
                    >
                        {{ t.label }}
                    </button>
                </div>
            </div>

            <OverviewTab
                v-if="tab === 'overview'"
                :detail="detail"
                :event-labels="eventLabels"
                :last-run="lastRun"
                @goto-history="tab = 'history'"
            />
            <TargetsTab v-else-if="tab === 'targets'" :detail="detail" />
            <ExceptionsTab v-else-if="tab === 'exceptions'" :detail="detail" />
            <HistoryTab v-else :detail="detail" />

            <div class="sc-detail-foot">
                <span>{{ $t("관리자") }} {{ detail.regWorker || "-" }}</span>
                <span>{{ $t("생성") }} {{ detail.regDate || "-" }}</span>
                <span>{{ $t("수정") }} {{ detail.modDate || "-" }}</span>
            </div>
        </template>
    </section>
</template>

<script>
import OverviewTab from "./detail/OverviewTab.vue";
import TargetsTab from "./detail/TargetsTab.vue";
import ExceptionsTab from "./detail/ExceptionsTab.vue";
import HistoryTab from "./detail/HistoryTab.vue";
import { fetchScheduleDetail, fillTargetNames, resolveEventLabels, ApiError } from "../utils/scheduleApi";
import { zoneName } from "../utils/scheduleFormat";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: { OverviewTab, TargetsTab, ExceptionsTab, HistoryTab },
    props: {
        schdCtrlIdx: { type: [Number, String], default: null },
        /** 목록 행 (마지막 실행 정보는 목록 API 에만 있다) */
        listRow: { type: Object, default: null },
    },
    data() {
        return {
            detail: null,
            eventLabels: [],
            tab: "overview",
            loading: false,
        };
    },
    computed: {
        zoneLabel() {
            return zoneName(this.$store, this.detail && this.detail.zoneIdx);
        },
        lastRun() {
            const row = this.listRow;
            if (!row || !row.lastCtrlDate) return null;
            return { date: row.lastCtrlDate, ok: row.lastSuccessCnt ?? 0, fail: row.lastFailCnt ?? 0 };
        },
        tabs() {
            return [
                { key: "overview", label: this.$t("개요") },
                { key: "targets", label: this.$t("제어 대상") },
                { key: "exceptions", label: this.$t("예외일") },
                { key: "history", label: this.$t("실행 이력") },
            ];
        },
    },
    watch: {
        schdCtrlIdx: {
            immediate: true,
            handler(idx) {
                this.tab = "overview";
                this.load(idx);
            },
        },
    },
    methods: {
        async load(idx = this.schdCtrlIdx) {
            if (!idx) {
                this.detail = null;
                return;
            }
            this.loading = true;
            try {
                const detail = await fetchScheduleDetail(idx);
                await fillTargetNames(detail);
                // 응답 대기 중 다른 스케줄로 바뀐 경우 버린다
                if (idx !== this.schdCtrlIdx) return;
                this.detail = detail;
                this.eventLabels = [];
                resolveEventLabels(detail.events).then((labels) => {
                    if (this.detail === detail) this.eventLabels = labels;
                });
            } catch (e) {
                if (idx !== this.schdCtrlIdx) return;
                this.detail = null;
                if (e instanceof ApiError) this.alertWarning(e.message, "", e.detail);
                else this.alertNoti(popupMessages.COMMON_NO_SEARCH_DATA_POPUP_MESSAGE);
                console.error(e);
            } finally {
                if (idx === this.schdCtrlIdx) this.loading = false;
            }
        },
        reload() {
            return this.load();
        },
    },
};
</script>
